import React, { SetStateAction, useState } from "react";

import { Card, Divider } from "@mui/material";
import {
	NeutralColor,
	PrimaryColor,
	SemanticColor,
} from "node-library/dist/colors";
import {
	Severity,
	TestHierarchyNodeType,
	TurnaroundTime,
} from "node-library/dist/constants";
import {
	FrontendCompanyResultFieldHierarchyNodeMappingPackage,
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyTestHierarchyNode,
	FrontendCompanyTestPackage,
	FrontendCompanyTestResultField,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleTestInformation,
} from "node-library/dist/portal";
import { useTranslation } from "react-i18next";
import {
	GreenButton,
	GreyButton,
	TextButton,
} from "../../../common-components/button";
import {
	CommonSwitch,
	CustomCheckbox,
	CustomRadioGroup,
	CustomTextInput,
	StandardRadioGroup,
} from "../../../common-components/inputs";

import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { YellowWarnIcon } from "../../../common-components/toast/warnIcons";
import AddIcon from "@mui/icons-material/Add";
import { FrontendExpectationInputState } from "node-library/dist/frontendInputStates";
import EditInstanceExpectationModal from "../../../common-components/modals/editInstanceExpectationModal";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	orderTestCategory: FrontendCompanyTestCategory;

	sampleName: string;
	lotCode: string;

	sampleMatrix: FrontendCompanySampleMatrix | undefined;

	otherInstructions: string;
	setOtherInstructions: React.Dispatch<React.SetStateAction<string>>;

	isRetest: boolean;

	setCurrentPageIndex: (value: number) => void;
	treatment: boolean;
	setTreatment: React.Dispatch<React.SetStateAction<boolean>>;
	selectedPackage: FrontendCompanyTestPackage | undefined;
	setSelectedPackageId: React.Dispatch<React.SetStateAction<string>>;
	sampleTestInfo: FrontendSampleTestInformation;
	setSampleTestInfo: React.Dispatch<
		React.SetStateAction<FrontendSampleTestInformation>
	>;

	turnaroundTime: TurnaroundTime;

	// showInGallery: boolean;
	// setShowInGallery: React.Dispatch<React.SetStateAction<boolean>>;
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
}

export default function StepFourSelectTestOptions(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		company,
		orderTestCategory,
		sampleMatrix,
		turnaroundTime,
		setCurrentPageIndex,
		sampleTestInfo,
		setUnsavedChanges,
	} = props;

	const sampleCategory: string = sampleMatrix
		? sampleMatrix.sampleCategory
		: "";

	const [treatment, setTreatment] = useState<boolean>(props.treatment);

	const selectedNodeIds: Array<string> = sampleTestInfo.selectedTests.map(
		(test) => {
			return test.selectedNodeId;
		},
	);
	const [selectedTestHierarchyNodeIds, setSelectedTestHierarchyNodeIds] =
		useState<Array<string>>(selectedNodeIds);

	const [additionalTestOrInstructions, setAdditionalTestOrInstructions] =
		useState<boolean>(
			props.otherInstructions.length > 0 || props.sampleTestInfo.additionalTest,
		);
	const [additionalTestDescription, setAdditionalTestDescription] =
		useState<string>(props.sampleTestInfo.additionalTestDescription);

	const [otherInstructions, setOtherInstructions] = useState<string>(
		props.otherInstructions,
	);

	const [selectedPackageId, setSelectedPackageId] = useState<string>(
		props.selectedPackage?.packageId || "",
	);

	const [showExpectationModal, setShowExpectationModal] = useState(false);

	const [expectationInputs, setExpectationInputs] =
		useState<FrontendExpectationInputState>(
			props.sampleTestInfo.expectationInputs,
		);

	//validations
	const [additionalTestError, setAdditionalTestError] = useState<string>("");
	const [otherInstructionsError, setOtherInstructionsError] =
		useState<string>("");

	//[parent node id, [child nodes] ]
	const hierarchyNodeParentMap = new Map<
		string,
		FrontendCompanyTestHierarchyNode[]
	>();

	orderTestCategory.hierarchyNodes.forEach((node) => {
		const parentId = node.parentNodeId || "root";
		if (!hierarchyNodeParentMap.has(parentId))
			hierarchyNodeParentMap.set(parentId, [node]);
		else {
			hierarchyNodeParentMap.get(parentId)!.push(node);
		}
	});

	const fieldsHavingExpectations: FrontendCompanyTestResultField[] =
		company.testResultAndExpectationFields.filter((resultField) => {
			return resultField.expectationField != undefined;
		});

	const fieldsNeedingExpectations: FrontendCompanyTestResultField[] =
		fieldsHavingExpectations
			.filter((resultField) => {
				// 1. Get the actual package objects from the company data using the IDs stored in the field
				const associatedPackages: Array<FrontendCompanyResultFieldHierarchyNodeMappingPackage> =
					company.hierarchyNodeMappingPackages.filter((pkg) =>
						resultField.nodeMappingPackageIds.includes(pkg.packageId),
					);

				// 2. Check if ANY of those packages have ALL their required nodes selected
				return associatedPackages.some((mappingPackage) =>
					mappingPackage.mappingNodeIds.every((id) =>
						selectedTestHierarchyNodeIds.includes(id),
					),
				);
			})
			.sort((a, b) => a.displayOrder - b.displayOrder);
	//turnaround times

	// const [showInGallery, setShowInGallery] = useState<boolean>(
	// 	props.showInGallery
	// );
	const hasAdditionalTest: boolean =
		additionalTestOrInstructions &&
		typeof additionalTestDescription === "string" &&
		additionalTestDescription.trim().length > 0;

	let packages: Array<FrontendCompanyTestPackage> =
		orderTestCategory.testPackages
			.filter((pack) => {
				return pack.sampleCategory == sampleCategory;
			})
			.sort((a, b) => a.displayIndex - b.displayIndex);

	//custom package
	packages.push({
		packageId: "",
		packageName: translation("Custom"),
		packageKey: "Custom",
		sampleCategory: "",
		displayIndex: 999,
		packageTestNodeIds: [],
	});

	const handlePackageChange = (event: any) => {
		const packageIdSelected: string = event.target.value;
		setSelectedPackageId(packageIdSelected);
		const packageSelected = orderTestCategory.testPackages.find((pack) => {
			return pack.packageId == packageIdSelected;
		});
		if (packageSelected) {
			const packageTests: Array<string> = packageSelected.packageTestNodeIds;
			setSelectedTestHierarchyNodeIds(packageTests);
		}

		setUnsavedChanges(true);
	};

	const packageSelectionOptions: Array<{
		key: string;
		name: string;
		description?: string;
		value: any;
	}> = packages.map((testPackageInfo: FrontendCompanyTestPackage) => {
		return {
			key: testPackageInfo.packageId,
			name: testPackageInfo.packageName,
			description: "",
			value: testPackageInfo.packageId,
			children: (
				<div
					className={`center-text test-package-radio-option ${
						selectedPackageId === testPackageInfo.packageId
							? "test-package-radio-option-selected"
							: ""
					}`}
				>
					{testPackageInfo.packageName}
				</div>
			),
		};
	});
	const packageSelection: React.ReactNode = (
		<Card className=" card-border-radius-14p card-small-padding">
			<CustomRadioGroup
				options={packageSelectionOptions}
				selectedOption={selectedPackageId}
				onChange={handlePackageChange}
				className="test-package-radio-group"
			/>
		</Card>
	);

	const handleSubmitPackageInfo = () => {
		setAdditionalTestError("");
		setOtherInstructionsError("");

		//validate if any settings are enabled
		let noTests: boolean =
			!hasAdditionalTest && selectedTestHierarchyNodeIds.length == 0;

		noTests &&= !orderTestCategory.hierarchyNodes.some((node) => {
			return (
				node.nodeType === TestHierarchyNodeType.TEST &&
				selectedTestHierarchyNodeIds.includes(node.nodeId)
			);
		});

		if (noTests) {
			openSnackbar(
				translation("MissingInformation"),
				Severity.WARN,
				translation("NoTestError"),
				<YellowWarnIcon />,
			);
			return;
		}
		const testGroupWithTests: Set<string> = new Set();

		//validate radio fields
		for (const node of orderTestCategory.hierarchyNodes) {
			if (node.nodeType === TestHierarchyNodeType.TEST) {
				if (!selectedTestHierarchyNodeIds.includes(node.nodeId)) continue;
				testGroupWithTests.add(node.parentNodeId as string);

				const children = hierarchyNodeParentMap.get(node.nodeId) || [];
				const radioGroups = children.filter(
					(child) =>
						child.nodeType === TestHierarchyNodeType.TEST_OPTION_RADIO_GROUP,
				);

				for (const radioGroup of radioGroups) {
					const options = hierarchyNodeParentMap.get(radioGroup.nodeId) || [];
					const hasSelectedOption = options.some((option) =>
						selectedTestHierarchyNodeIds.includes(option.nodeId),
					);

					if (!hasSelectedOption) {
						openSnackbar(
							translation("MissingInformation"),
							Severity.WARN,
							translation("TestMissingInformationError"),
							<YellowWarnIcon />,
						);
						return;
					}
				}
			}
		}
		for (const testNodeId of Array.from(testGroupWithTests)) {
			const children: Array<FrontendCompanyTestHierarchyNode> =
				hierarchyNodeParentMap.get(
					testNodeId,
				) as Array<FrontendCompanyTestHierarchyNode>;
			for (const child of children) {
				if (
					child.nodeType === TestHierarchyNodeType.TEST_GROUP_OPTION_RADIO_GROUP
				) {
					//validate
					const radioOptions = hierarchyNodeParentMap.get(child.nodeId) || [];
					const hasSelectedChild = radioOptions.some((option) =>
						selectedTestHierarchyNodeIds.includes(option.nodeId),
					);
					if (!hasSelectedChild) {
						openSnackbar(
							translation("MissingInformation"),
							Severity.WARN,
							translation("TestMissingInformationError"),
							<YellowWarnIcon />,
						);
						return;
					}
				}
				//otherwise no-op
			}
		}

		if (
			additionalTestOrInstructions &&
			(!additionalTestDescription || additionalTestDescription.length === 0) &&
			(!otherInstructions || otherInstructions.length === 0)
		) {
			setAdditionalTestError(translation("MissingInformation"));
			setOtherInstructionsError(translation("MissingInformation"));

			openSnackbar(
				translation("MissingInformation"),
				Severity.WARN,
				translation("MissingAdditionalTestOrInstructionsError"),
				<YellowWarnIcon />,
			);
			return;
		}

		const sampleTestInfo: FrontendSampleTestInformation = {
			selectedTests: selectedTestHierarchyNodeIds.map((id) => {
				return { selectedNodeId: id, assignedBatchId: undefined };
			}),
			additionalTest: hasAdditionalTest,
			additionalTestDescription: hasAdditionalTest
				? additionalTestDescription
				: "",
			expectationInputs,

			turnaroundTime: turnaroundTime,
		};
		props.setTreatment(treatment);

		props.setSampleTestInfo(sampleTestInfo);

		props.setSelectedPackageId(selectedPackageId || "");
		props.setOtherInstructions(otherInstructions);

		// props.setShowInGallery(showInGallery);
		setCurrentPageIndex(4);
	};

	function changeTestOption(nodeId: string, value: boolean) {
		setSelectedTestHierarchyNodeIds((prev) => {
			const isSelected = prev.includes(nodeId);
			let newSelectedIds;

			if (isSelected) {
				newSelectedIds = prev.filter((id) => id !== nodeId);
			} else {
				newSelectedIds = [...prev, nodeId];
			}

			return newSelectedIds;
		});
		setUnsavedChanges(true);
	}

	const treatmentSampleSwitch: React.ReactNode = (
		<div className="common-card-header">
			<div>
				<label className="typography-body-01">
					{translation("IrradiationUVTreatment")}
				</label>
				<div
					style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
					className="typography-caption"
				>
					{translation("TreatmentDescription")}
				</div>
			</div>

			<CommonSwitch
				id={"treatment-switch"}
				checked={treatment}
				setChecked={(value) => {
					setTreatment(value);
					setUnsavedChanges(true);
				}}
			/>
		</div>
	);
	const treatmentSwitchContainer: React.ReactNode = (
		<Card className="sample-info-collapsible-card card-medium-padding">
			{treatmentSampleSwitch}
		</Card>
	);

	//begin rendering hierarchy nodes

	const toggleCheckbox = (id: string) => {
		setSelectedTestHierarchyNodeIds((prev) =>
			prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
		);
	};

	const setRadioSelection = (groupParentId: string, selectedId: string) => {
		//remove the sibling selections, and add the selected one
		const siblingIds = (hierarchyNodeParentMap.get(groupParentId) || []).map(
			(n) => n.nodeId,
		);
		setSelectedTestHierarchyNodeIds((prev) => [
			...prev.filter((id) => !siblingIds.includes(id)),
			selectedId,
		]);
	};

	//render function - recursive
	function renderHierarchyNodesHelper(
		currentNode: FrontendCompanyTestHierarchyNode,
	): React.ReactNode {
		const children: Array<FrontendCompanyTestHierarchyNode> = (
			hierarchyNodeParentMap.get(currentNode.nodeId) || []
		).sort((a, b) => a.nodeDisplayOrder - b.nodeDisplayOrder);
		let currentNodeToRender: React.ReactNode = <></>;
		switch (currentNode.nodeType) {
			case TestHierarchyNodeType.TEST_GROUP:
				return (
					<div key={currentNode.nodeId}>
						<div className="typography-body-01" style={{ marginLeft: "10px" }}>
							{currentNode.nodeName}
							<div className="test-options-container">
								{children.map((child) => renderHierarchyNodesHelper(child))}
							</div>
						</div>
					</div>
				);
			case TestHierarchyNodeType.TEST_GROUP_OPTION_RADIO_GROUP:
				const radioOptions: Array<{
					key: string;
					name: string;
					value: any;
				}> = children.map((child) => {
					return {
						key: child.nodeId,
						name: child.nodeName,
						value: child.nodeId,
					};
				});
				const selectedOption: string =
					children.find((child) =>
						selectedTestHierarchyNodeIds.includes(child.nodeId),
					)?.nodeId ||
					// currentNode.groupDefaultSelectedNodeId ||
					"";

				//render the children in radio group
				return (
					<div key={currentNode.nodeId} className="testing-method-radio-row">
						<div
							className="typography-body-02"
							style={{ color: SemanticColor.INFO_MEDIUM }}
						>
							{currentNode.nodeName}
						</div>

						{
							<StandardRadioGroup
								options={radioOptions}
								selectedOption={selectedOption}
								onChange={(e) => {
									setRadioSelection(currentNode.nodeId, e.target.value);
								}}
								className="test-additional-option-radio-group"
							/>
						}
					</div>
				);
			case TestHierarchyNodeType.TEST_GROUP_OPTION_CHECKBOX_GROUP:
				//unimplemented
				return null;

			case TestHierarchyNodeType.TEST:
				const testSelected: boolean = selectedTestHierarchyNodeIds.includes(
					currentNode.nodeId,
				);
				const showAdditionalOptions: boolean =
					testSelected &&
					children.some((child) => {
						return (
							child.nodeType === TestHierarchyNodeType.TEST_OPTION_RADIO_GROUP
						);
					});
				return (
					<div key={currentNode.nodeId} className="test-option-grid">
						<div
							style={{
								gridColumn: showAdditionalOptions ? "span 1" : "span 2",
							}}
						>
							<CustomCheckbox
								className={`test-option-checkbox ${
									testSelected && "test-option-checkbox-checked"
								}`}
								label={currentNode.nodeName}
								checked={testSelected}
								setChecked={() => {
									toggleCheckbox(currentNode.nodeId);
								}}
							/>
						</div>
						{showAdditionalOptions &&
							children.map((child) => renderHierarchyNodesHelper(child))}
					</div>
				);

			case TestHierarchyNodeType.TEST_OPTION_RADIO_GROUP:
				const testRadioOptions: Array<{
					key: string;
					name: string;
					value: any;
				}> = children.map((child) => {
					return {
						key: child.nodeId,
						name: child.nodeName,
						value: child.nodeId,
					};
				});
				const testSelectedOption: string =
					children.find((child) =>
						selectedTestHierarchyNodeIds.includes(child.nodeId),
					)?.nodeId ||
					currentNode.groupDefaultSelectedNodeId ||
					"";

				//render the children in radio group
				return (
					<StandardRadioGroup
						key={currentNode.nodeId}
						options={testRadioOptions}
						selectedOption={testSelectedOption}
						onChange={(e) => {
							setRadioSelection(currentNode.nodeId, e.target.value);
						}}
						className="test-additional-option-radio-group"
					/>
				);

			case TestHierarchyNodeType.TEST_ADDITIONAL_OPTION_GROUP:
				//checkbox group, unimplemented
				return null;

			case TestHierarchyNodeType.ROOT:
				return (
					<>{children.map((child) => renderHierarchyNodesHelper(child))}</>
				);
			case TestHierarchyNodeType.RADIO_OPTION:
			case TestHierarchyNodeType.CHECKBOX_OPTION:
				//handled in parent nodes, do not render
				return null;
		}
	}

	const hierarchyNodeComponent: React.ReactNode = renderHierarchyNodesHelper(
		(
			hierarchyNodeParentMap.get(
				"root",
			) as Array<FrontendCompanyTestHierarchyNode>
		)[0] as FrontendCompanyTestHierarchyNode,
	);
	//hierarchy node render ends

	//additional testing
	function setAdditionTestChecked() {
		if (!additionalTestOrInstructions) {
			setAdditionalTestOrInstructions(true);
			setUnsavedChanges(true);
		} else {
			setAdditionalTestOrInstructions(false);
			setOtherInstructions("");
			setUnsavedChanges(true);
		}
	}
	const additionalTestingField = {
		label: `${translation("AdditionalTesting")}:`,
		description: `${translation("AdditionalTestingDescription")}`,
		inputType: "checkbox",
		checked: additionalTestOrInstructions,
		setChecked: setAdditionTestChecked,
	};

	const additionalTestingComponent: React.ReactNode = (
		<Card className="card-border-radius-14p" style={{ padding: "16px 32px" }}>
			<div className="common-card-header">
				<div className="typography-heading-03">
					{translation("AdditionalTesting")}
				</div>
				<CommonSwitch
					id={"additional-test-switch"}
					checked={additionalTestingField.checked}
					setChecked={additionalTestingField.setChecked}
				/>
			</div>

			{additionalTestOrInstructions && (
				<div
					className="sample-other-testing-instruction-grid"
					style={{ marginTop: "10px" }}
				>
					<CustomTextInput
						id="additionalTestTextarea"
						value={additionalTestDescription}
						setValue={(value) => {
							setAdditionalTestDescription(value);
							setUnsavedChanges(true);
						}}
						label={translation("OtherTesting")}
						isTextArea
						errorMsg={additionalTestError}
						setErrorMsg={setAdditionalTestError}
					/>
					<CustomTextInput
						id="additionalTestTextarea"
						value={otherInstructions}
						setValue={(value) => {
							setOtherInstructions(value);
							setUnsavedChanges(true);
						}}
						label={translation("OtherInstructions")}
						isTextArea
						errorMsg={otherInstructionsError}
						setErrorMsg={setOtherInstructionsError}
					/>
				</div>
			)}
		</Card>
	);

	const sampleInfoCard: React.ReactNode = (
		<Card
			className="common-card card-border-radius-14p"
			style={{ height: "100%", margin: 0 }}
		>
			<div className="sample-info-card-content">
				<div className="typography-heading-03">
					{translation("SampleDetails")}
				</div>
				<div className="choose-package-sample-info-card">
					<div className="overflow-ellipsis-text typography-body-01">{`${translation(
						"Name",
					)}: ${props.sampleName}`}</div>
					<Divider
						className="choose-package-sample-info-first-divider"
						orientation="vertical"
						flexItem
					/>

					<div className="overflow-ellipsis-text typography-body-01">{`${translation(
						"Lot",
					)}: ${props.lotCode}`}</div>
					<Divider
						className="choose-package-sample-info-second-divider"
						orientation="vertical"
						flexItem
					/>

					<div className="choose-package-full-width-column typography-body-01">{`${translation(
						"Matrix",
					)}: ${sampleMatrix?.subMatrix}`}</div>
				</div>
			</div>
		</Card>
	);

	const sampleInfoCardConainer: React.ReactNode = (
		<div className="new-sample-info-card-container-grid">{sampleInfoCard}</div>
	);

	// const showInGallerySwitch: React.ReactNode = (
	// 	<Card style={{ padding: "16px 32px" }}>
	// 		<div className="common-card-header">
	// 			<div className="typography-heading-03">
	// 				{translation("ShowSampleInGallery")}
	// 			</div>
	// 			<CommonSwitch
	// 				checked={showInGallery}
	// 				setChecked={(value) => {
	// 					setShowInGallery(value);
	// 					setUnsavedChanges(true);
	// 				}}
	// 			/>
	// 		</div>
	// 	</Card>
	// );

	const stepFourTestSelectionComponent: JSX.Element = (
		<div>
			{sampleInfoCardConainer}

			<div style={{ marginTop: "30px" }}>
				<div className="typography-heading-03">
					{translation("NewSampleStepFourDescription")}
				</div>
				<div className="new-sample-choose-test-and-package-container">
					{packageSelection}
					<div
					// onSubmit={(e) => {
					// 	e.preventDefault();
					// 	handleSubmitPackageInfo();
					// }}
					>
						<Card
							className="common-card card-border-radius-14p"
							style={{
								height: "100%",
								margin: 0,
								padding: "15px 25px",
								border: `2px solid ${PrimaryColor.PRIMARY_LV5}`,
								boxSizing: "border-box",
							}}
						>
							{treatmentSwitchContainer}

							<div className="select-test-card-title-row">
								<div className="typography-body-01 typography-bold">
									{translation("CustomizePackageDescription")}
								</div>
								{fieldsNeedingExpectations.length > 0 && (
									<TextButton
										id={"add-test-report-email-button"}
										onClick={() => {
											setShowExpectationModal(true);
										}}
										size="medium"
									>
										<AddIcon style={{ fontSize: 32 }} />
										<span className="typography-body-01">
											{translation("TestExpectation")}
										</span>
									</TextButton>
								)}
							</div>

							<div className="test-selection-grid">
								{hierarchyNodeComponent}
							</div>
						</Card>
					</div>
					{additionalTestingComponent}
					{/* {showInGallerySwitch} */}
				</div>

				<div className="add-sample-form-button-panel">
					<GreyButton
						id="back-button"
						children={translation("Back")}
						onClick={() => setCurrentPageIndex(2)}
					></GreyButton>
					<GreenButton
						id="submit-package-info-button"
						children={translation("Next")}
						type="submit"
						onClick={() => handleSubmitPackageInfo()}
					></GreenButton>
				</div>
			</div>
			{
				<EditInstanceExpectationModal
					company={company}
					fieldsNeedingExpectations={fieldsNeedingExpectations}
					expectationInputs={expectationInputs}
					setExpectationInputs={setExpectationInputs}
					open={showExpectationModal}
					setOpen={setShowExpectationModal}
					setUnsavedChanges={setUnsavedChanges}
				></EditInstanceExpectationModal>
			}
		</div>
	);

	return (
		<div style={{ margin: "0 auto" }}>{stepFourTestSelectionComponent}</div>
	);
}
